import * as React from 'react';
import { Grid, createStyles, makeStyles } from '@material-ui/core';
import clsx from 'clsx';
import StringResourceManager, { GetString } from '../StringResourceManager';
import NotificationTypesListBox from './NotificationTypesListBox';
import enableIcon from '../../assets/images/arrow-right.png';
import RegisteredNotificationsListBox from './RegisteredNotificationsListBox';
import UserManagementModal from './UserManagementModal';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from '../../store';
import { useEffect } from 'react';
import {
  clearRegistrationSuccessMessage,
  clearUpdateSuccessMessage,
  fetchRegisteredUsers,
  resetNotificationsFilter,
} from '../../store/Notifications/NotificationsActions';
import { Dispatch } from 'redux';
import { Button } from '@mui/material';
import { Dialog } from '@progress/kendo-react-dialogs';
import NotificationRuleForm from './NotificationsRuleForm';
import { Fade } from '@progress/kendo-react-animation';
import {
  Notification,
  NotificationGroup,
} from '@progress/kendo-react-notification';
import {
  fetchPredMaintenanceRecords,
  fetchPrevMaintenanceRecords,
} from '../../store/Maintenance/MaintenanceActions';
import { fetchTickets } from '../../store/Tickets/TicketsActions';

const useStyles = makeStyles(theme =>
  createStyles({
    gridAlign: {
      display: 'flex',
      justifyContent: 'center',
    },
    margin: {
      margin: theme.spacing(1),
    },
    dialogLabel: {
      fontSize: '1.1rem',
      display: 'inline',
    },
    arrowIcon: {
      height: '50px',
      weight: '50px',
    },
    iconGrid: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      margin: 10,
    },
    listBoxGrid: {
      width: '250px',
    },
    notificationsListWidth: {
      width: '330px',
    },
    listBoxTitle: {
      height: 'auto',
      marginTop: '10px',
      marginBottom: '10px',
    },
    listBox: {
      border: '1px solid #000000',
      marginBottom: '10px',
    },
    bottomRight: {
      right: 0,
      bottom: 0,
      alignItems: 'flex-start',
      flexWrap: 'wrap-reverse',
    },
    send: {
      fontSize: '1.5rem',
    },
  })
);

interface Props {
  title?: string;
}

const NotificationsContainer: React.FC<Props> = ({
  title,
}): React.ReactElement => {
  const classes = useStyles();

  const [selectedNotification, setSelectedNotification] =
    React.useState<string>('');
  const [notificationName, setNotificationName] = React.useState('');
  const [notificationsModalVisible, setNotificationsModalVisible] =
    React.useState<boolean>(false);
  const [editView, setEditView] = React.useState(false);
  const [successMessage, displaySuccessMessage] = React.useState(false);
  const [updateSuccessMessage, displayUpdateSuccessMessage] =
    React.useState(false);

  const ADD_NOTIFICATION_RULE_BUTTON = GetString(
    'ADD_NOTIFICATION_RULE_BUTTON'
  );

  const registrationStatus: string = useSelector(
    (state: ApplicationState) =>
      state.notificationsReducer.registrationSuccessMessage
  );
  const updateStatus: string = useSelector(
    (state: ApplicationState) => state.notificationsReducer.updateSuccessMessage
  );

  const dispatch: Dispatch<any> = useDispatch();
  const handleNotificationSelect = (notification: string) => {
    setSelectedNotification(notification);
    fetchRegisteredUsers(dispatch);
    fetchTickets(dispatch, false, false);
    fetchPredMaintenanceRecords(dispatch, false);
    fetchPrevMaintenanceRecords(dispatch, false);
  };
  const handleOpen = () => {
    setNotificationsModalVisible(true);
  };
  const handleClose = () => {
    resetNotificationsFilter(dispatch);
    setEditView(false);
    setNotificationName('');
    setNotificationsModalVisible(false);
  };
  const editNotificationRule = async (name: string) => {
    setEditView(true);
    setNotificationName(name);
    setNotificationsModalVisible(true);
  };
  const handleRegistrationMessage = () => {
    if (editView) {
      displayUpdateSuccessMessage(true);
    } else {
      displaySuccessMessage(true);
    }
  };
  const resetSuccessMessage = () => {
    clearRegistrationSuccessMessage(dispatch);
  };
  const resetUpdateMessage = () => {
    clearUpdateSuccessMessage(dispatch);
  };

  useEffect(() => {
    let timeoutId: any;
    if (successMessage) {
      timeoutId = setTimeout(() => {
        displaySuccessMessage(false);
        resetSuccessMessage();
      }, 3000);
    } else if (updateSuccessMessage) {
      timeoutId = setTimeout(() => {
        displayUpdateSuccessMessage(false);
        resetUpdateMessage();
      }, 3000);
    }
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [successMessage, updateSuccessMessage]);

  return (
    <React.Fragment>
      <Grid className={clsx(classes.margin)}>
        <UserManagementModal />
      </Grid>
      <Grid container spacing={2}>
        <Grid className={classes.margin}>
          <StringResourceManager
            variant="body1"
            style="commandLabel"
            stringKey={title}
            gutterBottom={true}
          />
        </Grid>
        <Grid className={clsx(classes.gridAlign, classes.margin)}>
          <Grid className={classes.listBoxGrid}>
            <Grid className={classes.listBoxTitle}>
              <StringResourceManager
                variant="body1"
                style="dialogLabel"
                stringKey={'SELECT_NOTIFICATION_TYPE'}
                gutterBottom={true}
              />
            </Grid>
            <Grid className={classes.listBox}>
              <NotificationTypesListBox onSelect={handleNotificationSelect} />
            </Grid>
          </Grid>
          <Grid
            className={classes.iconGrid}
            hidden={selectedNotification === ''}
          >
            <img src={enableIcon} className={classes.arrowIcon} />
          </Grid>
        </Grid>

        {selectedNotification && (
          <Grid className={(classes.gridAlign, classes.margin)}>
            <Grid className={classes.notificationsListWidth}>
              <Grid className={classes.listBoxTitle}>
                <StringResourceManager
                  variant="body1"
                  style="dialogLabel"
                  stringKey={'REGISTERED_NOTIFICATIONS'}
                  gutterBottom={true}
                />
              </Grid>
              <Grid
                className={classes.listBox}
                hidden={selectedNotification === ''}
              >
                <RegisteredNotificationsListBox
                  handleEditNotification={editNotificationRule}
                  notificationType={selectedNotification}
                />
              </Grid>
              <Grid className={clsx(classes.gridAlign, classes.margin)}>
                <Button variant="contained" onClick={handleOpen}>
                  {ADD_NOTIFICATION_RULE_BUTTON}
                </Button>
              </Grid>
            </Grid>

            {notificationsModalVisible && (
              <Dialog
                onClose={handleClose}
                title={
                  <StringResourceManager
                    stringKey="PAGE_TITLE_NOTIFICATION_RULES"
                    style="dialogTitle"
                    variant="h1"
                  />
                }
                width="650px"
              >
                <NotificationRuleForm
                  notificationType={selectedNotification}
                  notificationName={notificationName}
                  editView={editView}
                  closeForm={handleClose}
                  showRegistrationStatus={handleRegistrationMessage}
                ></NotificationRuleForm>
              </Dialog>
            )}
          </Grid>
        )}
      </Grid>
      <Fade>
        <NotificationGroup className={classes.bottomRight}>
          {successMessage && registrationStatus && (
            <Notification
              className={classes.send}
              type={{
                style: registrationStatus === 'success' ? 'success' : 'error',
                icon: true,
              }}
              closable={true}
            >
              {
                <StringResourceManager
                  stringKey={
                    registrationStatus === 'success'
                      ? 'NOTIFICATION_REGISTRATION_SUCCESS_MESSAGE'
                      : 'NOTIFICATION_REGISTRATION_FAILED_MESSAGE'
                  }
                />
              }
            </Notification>
          )}
          {updateSuccessMessage && updateStatus && (
            <Notification
              className={classes.send}
              type={{
                style: updateStatus === 'success' ? 'success' : 'error',
                icon: true,
              }}
              closable={true}
            >
              {
                <StringResourceManager
                  stringKey={
                    updateStatus === 'success'
                      ? 'NOTIFICATION_UPDATE_SUCCESS_MESSAGE'
                      : 'NOTIFICATION_UPDATE_FAILED_MESSAGE'
                  }
                />
              }
            </Notification>
          )}
        </NotificationGroup>
      </Fade>
    </React.Fragment>
  );
};

export default NotificationsContainer;
